import { solveLinearSystem, matMul, matTranspose, matVecMul, nullspaceBasis, vecDot } from './sketch_wall_math.js?v=20260706a';
import {
  allSketchNodes,
  allSketchWalls,
  allSketchAngles,
  allSketchStraightJoints,
  sketchNodeById,
  sketchWallById,
  sketchWallOtherNodeId,
  sketchWallSideLength,
  sketchWallAbsoluteAngle,
  reclampSketchWallOpenings
} from './sketch_wall_state.js?v=20260904f';

const LENGTH_TOLERANCE_MM = 0.01;
const ANGLE_TOLERANCE_RAD = (0.005 * Math.PI) / 180; // ~0.005°
const MAX_ITERATIONS = 150;
const FINITE_DIFF_STEP = 0.5; // mm — perturbation step for numeric Jacobians

function wrapAngle(radians) {
  let value = radians;
  while (value <= -Math.PI) value += Math.PI * 2;
  while (value > Math.PI) value -= Math.PI * 2;
  return value;
}

// Builds the free-variable index (nodeId -> {xi, zi} slot in the flat
// variable vector). Fixed nodes and nodes with no walls are excluded.
function buildVariableIndex(options = {}) {
  const lockedNodeIds = options.lockedNodeIds instanceof Set
    ? options.lockedNodeIds
    : new Set(options.lockedNodeIds || []);
  const index = new Map();
  let cursor = 0;
  for (const node of allSketchNodes()) {
    if (node.fixed || lockedNodeIds.has(node.id)) continue;
    index.set(node.id, { xi: cursor, zi: cursor + 1 });
    cursor += 2;
  }
  return { index, size: cursor };
}

function makePositionReader(index, v) {
  return nodeId => {
    const node = sketchNodeById(nodeId);
    if (!node) return { x: 0, z: 0 };
    const slot = index.get(nodeId);
    if (!slot) return { x: node.x, z: node.z };
    return { x: v[slot.xi], z: v[slot.zi] };
  };
}

function vectorFromNodes(index) {
  const v = new Array(indexSize(index));
  for (const [nodeId, slot] of index) {
    const node = sketchNodeById(nodeId);
    v[slot.xi] = node.x;
    v[slot.zi] = node.z;
  }
  return v;
}

function indexSize(index) {
  let max = 0;
  for (const slot of index.values()) max = Math.max(max, slot.xi + 1, slot.zi + 1);
  return max;
}

function buildConstraintList() {
  const constraints = [];
  for (const wall of allSketchWalls()) {
    if (Number.isFinite(wall.length) && (wall.lengthMode === 'inner' || wall.lengthMode === 'outer')) {
      constraints.push({
        type: wall.lengthMode === 'inner' ? 'innerLength' : 'outerLength',
        ref: wall,
        target: wall.length,
        tolerance: LENGTH_TOLERANCE_MM
      });
    }
    if (wall.horizontal) {
      constraints.push({ type: 'horizontal', ref: wall, tolerance: LENGTH_TOLERANCE_MM });
    }
    if (wall.vertical) {
      constraints.push({ type: 'vertical', ref: wall, tolerance: LENGTH_TOLERANCE_MM });
    }
    if (wall.fixed && Number.isFinite(wall.fixedAngle)) {
      constraints.push({ type: 'fixedAngle', ref: wall, target: wall.fixedAngle, tolerance: ANGLE_TOLERANCE_RAD });
    }
  }
  for (const angle of allSketchAngles()) {
    if (Number.isFinite(angle.value)) {
      constraints.push({ type: 'angle', ref: angle, target: angle.value, tolerance: ANGLE_TOLERANCE_RAD });
    }
  }
  for (const joint of allSketchStraightJoints()) {
    constraints.push({ type: 'straight', ref: joint, tolerance: LENGTH_TOLERANCE_MM });
  }
  return constraints;
}

function residualFor(constraint, getPoint) {
  if (constraint.type === 'length') {
    const wall = constraint.ref;
    const a = getPoint(wall.startNodeId);
    const b = getPoint(wall.endNodeId);
    return Math.hypot(b.x - a.x, b.z - a.z) - constraint.target;
  }
  if (constraint.type === 'innerLength') {
    return sketchWallSideLength(constraint.ref, getPoint, 1) - constraint.target;
  }
  if (constraint.type === 'outerLength') {
    return sketchWallSideLength(constraint.ref, getPoint, -1) - constraint.target;
  }
  if (constraint.type === 'horizontal') {
    const wall = constraint.ref;
    const a = getPoint(wall.startNodeId);
    const b = getPoint(wall.endNodeId);
    return b.z - a.z;
  }
  if (constraint.type === 'vertical') {
    const wall = constraint.ref;
    const a = getPoint(wall.startNodeId);
    const b = getPoint(wall.endNodeId);
    return b.x - a.x;
  }
  if (constraint.type === 'angle') {
    const angle = constraint.ref;
    return wrapAngle(signedAngleAt(angle, getPoint) - constraint.target);
  }
  if (constraint.type === 'fixedAngle') {
    return wrapAngle(sketchWallAbsoluteAngle(constraint.ref, getPoint) - constraint.target);
  }
  if (constraint.type === 'straight') {
    return straightResidual(constraint.ref, getPoint);
  }
  return 0;
}

// Locks a straightJoint's two walls exactly collinear through their shared
// node. Deliberately NOT expressed as a 180° 'angle' constraint: that residual
// runs through wrapAngle/atan2, which has a discontinuity at exactly ±π — the
// one value a straight join actually needs — making the numeric Jacobian
// unreliable right there and the solver unable to converge (in practice only
// ~179°/181° would ever settle). A signed perpendicular-distance-from-line
// residual has no such wraparound: it's smooth everywhere and reads 0 exactly
// at perfectly straight, in the same mm units/tolerance as length constraints.
function straightResidual(joint, getPoint) {
  const node = getPoint(joint.nodeId);
  const wallA = sketchWallById(joint.wallAId);
  const wallB = sketchWallById(joint.wallBId);
  if (!wallA || !wallB) return 0;
  const otherA = getPoint(sketchWallOtherNodeId(wallA, joint.nodeId));
  const otherB = getPoint(sketchWallOtherNodeId(wallB, joint.nodeId));
  const dirLen = Math.hypot(otherA.x - node.x, otherA.z - node.z) || 1;
  const dirX = (otherA.x - node.x) / dirLen;
  const dirZ = (otherA.z - node.z) / dirLen;
  const relX = otherB.x - node.x;
  const relZ = otherB.z - node.z;
  return dirX * relZ - dirZ * relX;
}

function signedAngleAt(angle, getPoint) {
  const node = getPoint(angle.nodeId);
  const wallA = sketchWallById(angle.wallAId);
  const wallB = sketchWallById(angle.wallBId);
  if (!wallA || !wallB) return 0;
  const otherA = getPoint(sketchWallOtherNodeId(wallA, angle.nodeId));
  const otherB = getPoint(sketchWallOtherNodeId(wallB, angle.nodeId));
  const angleA = Math.atan2(otherA.z - node.z, otherA.x - node.x);
  const angleB = Math.atan2(otherB.z - node.z, otherB.x - node.x);
  return wrapAngle(angleB - angleA);
}

function residualVector(v, index, constraints) {
  const getPoint = makePositionReader(index, v);
  return constraints.map(constraint => residualFor(constraint, getPoint));
}

function numericJacobian(v, index, constraints) {
  const n = v.length;
  const base = residualVector(v, index, constraints);
  const columns = [];
  for (let j = 0; j < n; j += 1) {
    const plus = [...v];
    const minus = [...v];
    plus[j] += FINITE_DIFF_STEP;
    minus[j] -= FINITE_DIFF_STEP;
    const rPlus = residualVector(plus, index, constraints);
    const rMinus = residualVector(minus, index, constraints);
    columns.push(rPlus.map((value, i) => (value - rMinus[i]) / (2 * FINITE_DIFF_STEP)));
  }
  // columns[j][i] -> transpose into rows[i][j]
  const rows = base.map((_, i) => columns.map(col => col[i]));
  return rows;
}

function gradientOf(fn, v) {
  const n = v.length;
  const grad = new Array(n);
  for (let j = 0; j < n; j += 1) {
    const plus = [...v];
    const minus = [...v];
    plus[j] += FINITE_DIFF_STEP;
    minus[j] -= FINITE_DIFF_STEP;
    grad[j] = (fn(plus) - fn(minus)) / (2 * FINITE_DIFF_STEP);
  }
  return grad;
}

function isConverged(residuals, constraints) {
  return residuals.every((value, i) => Math.abs(value) <= constraints[i].tolerance);
}

// Levenberg-Marquardt: solves for the free-node positions that satisfy every
// active constraint residual, preferring the smallest move from the current
// position when the system is underdetermined (which it usually is, since
// most sketches start with far more free coordinates than constraints).
function variableMovementWeights(index, options = {}) {
  const configured = options.movementWeights instanceof Map
    ? options.movementWeights
    : new Map(Object.entries(options.movementWeights || {}));
  const weights = new Array(indexSize(index)).fill(1);
  for (const [nodeId, slot] of index) {
    const weight = Math.max(1e-6, Number(configured.get(nodeId)) || 1);
    weights[slot.xi] = weight;
    weights[slot.zi] = weight;
  }
  return weights;
}

function dampedMatrix(matrix, lambda, movementWeights) {
  return matrix.map((row, i) => row.map((value, k) => (
    i === k ? value + lambda * movementWeights[i] : value
  )));
}

function solveConstraints(index, constraints, options = {}) {
  let v = vectorFromNodes(index);
  const n = v.length;
  if (!n || !constraints.length) {
    return { v, converged: isConverged(residualVector(v, index, constraints), constraints) };
  }
  const movementWeights = variableMovementWeights(index, options);
  let lambda = 1e-2;
  let residuals = residualVector(v, index, constraints);
  for (let iter = 0; iter < MAX_ITERATIONS; iter += 1) {
    if (isConverged(residuals, constraints)) return { v, converged: true };
    const jacobian = numericJacobian(v, index, constraints);
    const jt = matTranspose(jacobian);
    const jtj = matMul(jt, jacobian);
    const jtr = matVecMul(jt, residuals);
    let accepted = false;
    for (let attempt = 0; attempt < 12 && !accepted; attempt += 1) {
      const dx = solveLinearSystem(dampedMatrix(jtj, lambda, movementWeights), jtr.map(value => -value));
      if (!dx) {
        lambda *= 4;
        continue;
      }
      const candidate = v.map((value, i) => value + dx[i]);
      const candidateResiduals = residualVector(candidate, index, constraints);
      const currentNorm = residuals.reduce((sum, r) => sum + r * r, 0);
      const candidateNorm = candidateResiduals.reduce((sum, r) => sum + r * r, 0);
      if (candidateNorm <= currentNorm || candidateNorm < 1e-10) {
        v = candidate;
        residuals = candidateResiduals;
        lambda = Math.max(lambda / 3, 1e-8);
        accepted = true;
      } else {
        lambda *= 4;
      }
    }
    if (!accepted) break;
  }
  return { v, converged: isConverged(residuals, constraints) };
}

// For every wall length and every angle, determines whether its value is
// fully implied by the current constraint set (regardless of whether the
// user explicitly typed it) by checking if the quantity's gradient has any
// component along the constraint Jacobian's nullspace — i.e. whether any
// remaining free motion of the sketch could still change it.
function classifyQuantities(index, constraints, v) {
  const jacobian = numericJacobian(v, index, constraints);
  const { basis } = nullspaceBasis(jacobian, v.length);

  const isFree = gradFn => {
    if (!basis.length) return false;
    const grad = gradientOf(gradFn, v);
    const gradNorm = Math.sqrt(vecDot(grad, grad));
    if (gradNorm < 1e-6) return false; // quantity doesn't depend on any free node at all
    let projection = 0;
    for (const direction of basis) projection += vecDot(grad, direction) ** 2;
    return Math.sqrt(projection) > gradNorm * 1e-3;
  };

  for (const wall of allSketchWalls()) {
    wall.innerFree = isFree(vec => sketchWallSideLength(wall, makePositionReader(index, vec), 1));
    wall.outerFree = isFree(vec => sketchWallSideLength(wall, makePositionReader(index, vec), -1));
    wall.free = wall.innerFree || wall.outerFree;
  }
  for (const angle of allSketchAngles()) {
    angle.free = isFree(vec => signedAngleAt(angle, makePositionReader(index, vec)));
  }
  // A node counts as draggable if either of its own coordinates can still
  // move under some allowed motion — even if a *measurement* through it
  // (like a wall length) is already fully defined, e.g. a chain anchored at
  // one end can still swing on its remaining angle freedom.
  for (const node of allSketchNodes()) {
    if (node.fixed || !index.has(node.id)) {
      node.free = false;
      continue;
    }
    const slot = index.get(node.id);
    node.free = isFree(vec => vec[slot.xi]) || isFree(vec => vec[slot.zi]);
  }
}

function applyVectorToNodes(index, v) {
  for (const [nodeId, slot] of index) {
    const node = sketchNodeById(nodeId);
    node.x = v[slot.xi];
    node.z = v[slot.zi];
  }
}

// Re-solves the whole sketch from its current constraint set. `ok` requires
// *every* constraint to be satisfied, not just whichever one the caller just
// edited — checking only the edited constraint let the solver silently
// accept a step where the rest of the sketch (e.g. previously-set 90° angles
// elsewhere) never actually re-converged, leaving distorted geometry next to
// dimension labels that still displayed the old, no-longer-true values.
function solveSketchWalls(options = {}) {
  const { index } = buildVariableIndex(options);
  const constraints = buildConstraintList();
  const solveConstraintsList = [...constraints, ...(options.additionalConstraints || [])];
  const result = options.validateOnly
    ? { v: vectorFromNodes(index), converged: isConverged(residualVector(vectorFromNodes(index), index, solveConstraintsList), solveConstraintsList) }
    : solveConstraints(index, solveConstraintsList, options);
  const { v, converged } = result;
  if (options.validateOnly && !converged) return { ok: false, converged: false };
  applyVectorToNodes(index, v);
  // Temporary edit pivots must guide movement only; they are not persistent
  // constraints and therefore must not make a node appear black/locked after
  // the solve. Reclassify against the real project freedoms.
  const { index: classificationIndex } = buildVariableIndex();
  classifyQuantities(classificationIndex, constraints, vectorFromNodes(classificationIndex));
  // Node positions (and therefore every wall's length) may just have
  // shifted — keep any Ausschnitte from silently drifting outside their
  // own wall's now-current bounds.
  for (const wall of allSketchWalls()) reclampSketchWallOpenings(wall);
  return { ok: converged, converged };
}

export { solveSketchWalls, wrapAngle };
